import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, UserPlus, Loader2, Sparkles, History } from "lucide-react";
import api, { formatApiErrorDetail } from "../lib/api";
import { MillieAvatar } from "./MascotBubble";
import { TASTES } from "./household/PeopleSteps";

export default function GuestSheet({ open, onClose, activeId, day, onPlan }) {
  const [name, setName] = useState("");
  const [taste, setTaste] = useState("simple");
  const [busy, setBusy] = useState(false);
  const [regulars, setRegulars] = useState([]);
  useEffect(() => {
    if (open && activeId) api.get(`/plan/guests/${activeId}`).then(({ data }) => setRegulars(data.guests || [])).catch(() => {});
  }, [open, activeId]);
  if (!open) return null;

  const add = async (gName = name, gTaste = taste) => {
    const who = (gName || "").trim() || "Guest";
    setBusy(true);
    try {
      const { data } = await api.post("/plan/guest", { household_id: activeId, day, name: who, taste: gTaste });
      onPlan(data);
      toast.success(data.swapped ? `Swapped ${day}'s dinner so ${who} will love it` : `${who} is on ${day}'s table`);
      setName(""); onClose();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const forget = async (g) => {
    try { await api.delete(`/plan/guests/${activeId}/${encodeURIComponent(g.name)}`); setRegulars((r) => r.filter((x) => x.name !== g.name)); toast.success(`Forgot ${g.name}`); }
    catch { toast.error("Couldn't do that"); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-sorted-ink/40 backdrop-blur-sm flex items-end justify-center" onClick={onClose} data-testid="guest-sheet">
      <div className="w-full max-w-md bg-[#FFF6F1] rounded-t-[2rem] p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3">
          <MillieAvatar size={44} float={false} mood="point" />
          <div className="flex-1">
            <p className="font-hand text-2xl text-sorted-ink leading-none">Who's joining on {day}?</p>
            <p className="text-xs text-sorted-slate font-semibold">I'll add a plate and make sure dinner suits them.</p>
          </div>
          <button onClick={onClose} aria-label="Close" data-testid="guest-close" className="w-9 h-9 rounded-full bg-white flex items-center justify-center text-sorted-slate"><X size={16} strokeWidth={3} /></button>
        </div>
        {regulars.length > 0 && (
          <div className="mt-4" data-testid="regular-guests">
            <p className="text-xs font-bold text-sorted-slate mb-2 flex items-center gap-1"><History size={12} strokeWidth={2.5} /> Your regulars — one tap</p>
            <div className="flex flex-wrap gap-2">
              {regulars.map((g) => (
                <span key={g.name} className="inline-flex items-center bg-white border-2 border-[#FDECEF] rounded-full pl-1 pr-1 py-1">
                  <button onClick={() => add(g.name, g.taste)} disabled={busy} data-testid={`regular-guest-${g.name}`} className="px-2 text-xs font-bold text-sorted-ink disabled:opacity-60">
                    {g.name} <span className="text-sorted-slate font-semibold">· {TASTES.find((t) => t.key === g.taste)?.label.split(" ")[0] || "Middle"}</span>
                  </button>
                  <button onClick={() => forget(g)} aria-label={`Forget ${g.name}`} data-testid={`regular-guest-forget-${g.name}`} className="w-5 h-5 rounded-full bg-muted text-sorted-slate flex items-center justify-center"><X size={10} strokeWidth={3} /></button>
                </span>
              ))}
            </div>
          </div>
        )}
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={40} data-testid="guest-name" placeholder="Name (e.g. Grandad)"
          className="mt-4 w-full bg-white rounded-2xl px-4 py-3 font-semibold text-sorted-ink outline-none focus:ring-2 focus:ring-sorted-peach border-2 border-[#FDECEF]" />
        <p className="text-xs font-bold text-sorted-slate mt-4 mb-2">Their taste</p>
        <div className="space-y-2" data-testid="guest-taste">
          {TASTES.map((t) => (
            <button key={t.key} type="button" onClick={() => setTaste(t.key)} data-testid={`guest-taste-${t.key}`}
              className={`w-full text-left rounded-2xl border-2 px-4 py-2.5 transition-colors ${taste === t.key ? "bg-sorted-ink border-sorted-ink text-white" : "bg-white border-[#FDECEF] text-sorted-ink"}`}>
              <span className="block font-bold text-sm">{t.label}</span>
              <span className={`block text-[11px] font-semibold ${taste === t.key ? "text-white/80" : "text-sorted-slate"}`}>{t.hint}</span>
            </button>
          ))}
        </div>
        <button onClick={() => add()} disabled={busy} data-testid="guest-add" className="mt-5 w-full bg-sorted-rose text-white font-bold py-4 rounded-full flex items-center justify-center gap-2 shadow-soft disabled:opacity-70">
          {busy ? <Loader2 size={17} className="animate-spin" /> : <UserPlus size={17} strokeWidth={2.5} />} {busy ? "Millie's planning around them…" : "Add guest & plan around them"}
        </button>
        <p className="mt-2 text-[11px] text-center text-sorted-slate font-semibold flex items-center justify-center gap-1"><Sparkles size={11} /> Millie keeps the dinner if it suits, or swaps it if it clearly won't.</p>
      </div>
    </div>
  );
}
